import { useEffect, useState } from 'react';
import { useScrollToBottom } from '../../../../utils/hooks/useScrollToBottom';
import { useAnimatedModal } from '../../../../utils/hooks/useAnimatedModal';
import axios from 'axios';

import { ErrorPage } from '../../../ErrorPage';
import { LoadingPage } from '../../../LoadingPage';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { getOrCreateBrowserKey } from '../../../../utils/fingerprint_utils';
import { Button } from '../components/Button';
import { PoweredByQITech } from '../components/PoweredByQITech';
import { CheckboxField } from '../components/CheckboxField';
import { DisclaimerBox } from '../components/DisclaimerBox';
import { DataCard } from '../components/DataCard';
import * as S from './styles';

interface ResumeOperationProps {
    signData: any;
    setSignerStep: (step: string) => void;
    setSignData: (data: any) => void;
}

const formatDate = (inputDate: string): string => {
    const date = new Date(inputDate);
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
};

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(value / 100);
};

const formatPercent = (value: number) =>
    value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '% ao mês';

export const CreditOperationAndSocialSecurityCardsResumeOperation = ({
    signData,
    setSignerStep,
    setSignData,
}: ResumeOperationProps) => {
    const { clientThemeSettings } = useThemeContext();
    const initialError = { has_error: false, status: 0 };

    const [error, setError] = useState<{ has_error: boolean; status: number }>(initialError);
    const [loading, setLoading] = useState(true);
    const [hasMadeRequest, setHasMadeRequest] = useState(false);
    const [isChecked, setIsChecked] = useState(false);

    const { contentEndRef, hasScrolledToEnd, scrollToBottom } = useScrollToBottom([loading]);
    const checkboxModal = useAnimatedModal();

    useEffect(() => {
        setLoading(false);
    }, []);

    const handleDownload = async (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        try {
            const response = await fetch(documentUrl);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'termo-contratacao-cartao-beneficio.pdf';
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            window.open(documentUrl, '_blank');
        }
    };

    const handleClick = async () => {
        if (hasMadeRequest) return;

        let browserKey: string | null = null;
        try {
            browserKey = await getOrCreateBrowserKey();
        } catch (_) {}

        const payload = {
            step: 'resume_operation',
            event_date: new Date().toISOString(),
            device_data: { device_key: browserKey },
        };

        const config = {
            headers: { Authorization: signData.signer_token },
        };

        setLoading(true);
        setHasMadeRequest(true);

        axios
            .patch('/signer', payload, config)
            .then((response) => {
                setSignerStep(response.data.next_step);
                setSignData({ ...signData, ...response.data });
            })
            .catch((error) => {
                const status = [401, 403, 409].includes((error.response || {}).status)
                    ? error.response.status
                    : 500;
                setError({ has_error: true, status });
                setHasMadeRequest(false);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    if (loading) {
        return <LoadingPage clientThemeSettings={clientThemeSettings} />;
    }

    const op = signData.credit_operation_data;
    const documentUrl = signData?.document_url;
    const operationType = op?.operation_type || 'structured_operation';
    const accentColor = clientThemeSettings?.font_color;

    return (
        <S.PageWrapper>
            <S.ScrollContent>
                <S.Header>
                    {clientThemeSettings?.logo_url && (
                        <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                    )}
                </S.Header>

                <S.HeroSection>
                    <S.Title $color={accentColor}>
                        <strong>Resumo</strong> do seu{'\n'}empréstimo
                    </S.Title>
                </S.HeroSection>

                <S.DataCardContainer>
                    <DataCard.Root>
                        {operationType === 'structured_operation' ? (
                            <>
                                <DataCard.Row
                                    label="Valor total do empréstimo"
                                    value={formatCurrency(op.issue_amount)}
                                    color={accentColor}
                                />
                                <DataCard.Row
                                    label="IOF"
                                    value={formatCurrency(op.iof)}
                                    color={accentColor}
                                />
                                <DataCard.Row
                                    label="Valor líquido a receber"
                                    value={<>{formatCurrency(op.disbursed_amount)}</>}
                                    color={accentColor}
                                />
                            </>
                        ) : (
                            <>
                                <DataCard.Row
                                    label={
                                        operationType === 'refinancing'
                                            ? 'Valor de pagamento de dívida'
                                            : 'Valor liberado - quitação de dívida'
                                    }
                                    value={formatCurrency(op.refinancing?.due_balance)}
                                    color={accentColor}
                                />
                                <DataCard.Row
                                    label={
                                        operationType === 'refinancing'
                                            ? 'Valor liberado ao emitente'
                                            : 'Valor liberado - livre utilização'
                                    }
                                    value={formatCurrency(op.refinancing?.final_disbursed_amount)}
                                    color={accentColor}
                                />
                            </>
                        )}
                        <DataCard.Row label="Taxa de juros (% a.m)" value={formatPercent(op.monthly_interest_rate || 0)} color={accentColor} />
                        <DataCard.Row label="CET (% a.m)" value={formatPercent(op.monthly_cet || 0)} color={accentColor} />
                        <DataCard.Row label="Número de parcelas mensais" value={op.number_of_installments} color={accentColor} />
                        <DataCard.Row label="Data da primeira parcela" value={formatDate(op.first_due_date)} color={accentColor} />
                        {op.interest_amount != null && (
                            <DataCard.Row label="Juros a pagar" value={formatCurrency(op.interest_amount)} color={accentColor} />
                        )}
                        {op.total_amount != null && (
                            <DataCard.Row label="Total a pagar" value={formatCurrency(op.total_amount)} color={accentColor} />
                        )}
                    </DataCard.Root>
                    {operationType === 'structured_operation' && (
                        <S.DisclaimerSection>
                            {documentUrl && (
                                <DisclaimerBox mode="info">
                                    Para mais informações,{' '}
                                    <a href={documentUrl} onClick={handleDownload}>
                                        consulte o contrato de empréstimo.
                                    </a>
                                </DisclaimerBox>
                            )}
                        </S.DisclaimerSection>
                    )}
                </S.DataCardContainer>

                <S.CheckboxWrapper>
                    <CheckboxField
                        id="resume-operation-terms"
                        checked={isChecked}
                        onChange={(e) => setIsChecked(e.target.checked)}
                        accentColor={accentColor}
                    >
                        <strong>Declaro que li e estou de acordo</strong> com as condições do contrato acima.
                    </CheckboxField>
                </S.CheckboxWrapper>
                <div ref={contentEndRef} />
            </S.ScrollContent>

            {error.has_error && (
                <ErrorPage
                    error={error}
                    redirectToLogin={() => setSignerStep('login')}
                    resetError={() => setError(initialError)}
                    clientThemeSettings={clientThemeSettings}
                />
            )}

            <S.FooterSection>
                <div
                    onClick={!hasScrolledToEnd ? scrollToBottom : !isChecked ? checkboxModal.open : undefined}
                    style={{ width: '100%' }}
                >
                    <Button
                        clientThemeSettings={clientThemeSettings}
                        disabled={!isChecked}
                        onClick={handleClick}
                    >
                        Aceitar termos de contrato
                    </Button>
                </div>

                <PoweredByQITech />
            </S.FooterSection>

            {checkboxModal.show && (
                <S.ModalOverlay $closing={checkboxModal.isClosing} onClick={checkboxModal.close}>
                    <S.ModalSheet $closing={checkboxModal.isClosing} onClick={(e) => e.stopPropagation()}>
                        <S.ModalTitle $color={accentColor}>Confirmação pendente</S.ModalTitle>
                        <S.ModalText>
                            Para aceitar os termos de contrato, você precisa <strong>marcar a caixa de confirmação</strong> acima.
                        </S.ModalText>
                        <S.ModalButtonRow>
                            <Button clientThemeSettings={clientThemeSettings} onClick={checkboxModal.close}>
                                Entendi
                            </Button>
                        </S.ModalButtonRow>
                    </S.ModalSheet>
                </S.ModalOverlay>
            )}
        </S.PageWrapper>
    );
};
